import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material'
import { useTransactions } from '../../contexts/TransactionsContext'
import { getMerchantInvoiceById, updateMerchantInvoice } from '../../utils/merchantToolStorage'

const BRAND_PURPLE = '#5B2EFF'
const BRAND_PURPLE_DARK = '#2A1B66'
const BRAND_ORANGE = '#FF8A00'
const SURFACE = 'rgba(27,16,64,0.96)'
const BORDER = '1px solid rgba(183,166,255,0.16)'

const money = (amount, currency) => `${currency} ${Number(amount || 0).toFixed(2)}`

const invoicePayLink = (invoice) => {
  const token = invoice?.public_token || invoice?.invoiceCode
  const fallback = `${window.location.origin}/pay/invoice/${encodeURIComponent(token || '')}`
  const saved = String(invoice?.pay_url || '').trim()
  if (!saved || saved.includes('pay.mojopayr.app')) return fallback
  return saved
}

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>{label}</Typography>
    <Typography sx={{ color: '#fff', fontWeight: 800, textAlign: 'right' }}>{value}</Typography>
  </Box>
)

export default function MerchantInvoiceDetail() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { addTransaction } = useTransactions()
  const invoice = useMemo(() => getMerchantInvoiceById(id), [id])

  const shareMessage = useMemo(() => {
    if (!invoice) return ''
    const payLink = invoicePayLink(invoice)
    return `MojoPayr payment request\nInvoice: ${invoice.invoiceCode}\nAmount: ${money(invoice.amount, invoice.currency)}\nPurpose: ${invoice.description || invoice.invoice_title}\nDue: ${invoice.due_date}\nUse Payment in MojoPayr and enter invoice code ${invoice.invoiceCode}, or open ${payLink}.`
  }, [invoice])

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {}
  }

  const recordPayment = () => {
    if (!invoice) return
    updateMerchantInvoice(invoice.id, { status: 'paid', paidAt: new Date().toISOString() })
    const tx = addTransaction({
      type: 'merchant_payment',
      status: 'success',
      amount: Number(invoice.amount || 0),
      fee: 0,
      total: Number(invoice.amount || 0),
      currency: invoice.currency,
      title: 'Payment Receipt',
      counterpart: invoice.customer_name,
      provider: invoice.merchant_name || 'MojoPayr Business',
      description: invoice.invoiceCode,
      account: invoice.customer_contact || invoice.customer_email || invoice.customer_phone || '-',
      createdAt: new Date().toISOString(),
    })
    navigate(`/receipt/${tx.id}`, { state: { transaction: tx } })
  }

  if (!invoice) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 6, background: SURFACE, border: BORDER }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ color: '#fff', fontWeight: 900 }}>Invoice not found.</Typography>
          </CardContent>
        </Card>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 30, md: 38 } }}>
          Invoice Detail
        </Typography>

        <Card sx={{ borderRadius: 6, overflow: 'hidden', background: `linear-gradient(135deg, ${BRAND_PURPLE_DARK} 0%, ${BRAND_PURPLE} 100%)` }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 18 }}>
              {money(invoice.amount, invoice.currency)}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.74)', mt: 1 }}>
              {invoice.invoice_title}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={1.25}>
              <Row label="Customer" value={invoice.customer_name} />
              <Row label="Contact" value={invoice.customer_contact || invoice.customer_email || invoice.customer_phone || '-'} />
              <Row label="Description" value={invoice.description || '-'} />
              <Row label="Payment ref" value={invoice.paymentRef} />
              <Row label="Created" value={new Date(invoice.createdAt).toLocaleString()} />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.5 }}>Share Request</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.8 }}>
              Share a ready-to-send payment message, invoice code, or payment link so the customer can settle it quickly in MojoPayr.
            </Typography>
            <Box sx={{ p: 2.2, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.04)', border: BORDER, color: 'rgba(255,255,255,0.82)', whiteSpace: 'pre-line' }}>
              {shareMessage}
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => copy(shareMessage)} sx={{ borderRadius: 3, textTransform: 'none', color: '#fff', borderColor: 'rgba(255,255,255,0.16)', flex: 1 }}>
                Copy Message
              </Button>
              <Button variant="contained" onClick={() => copy(invoicePayLink(invoice))} sx={{ borderRadius: 3, textTransform: 'none', bgcolor: BRAND_ORANGE, color: '#0B0620', flex: 1 }}>
                Copy Link
              </Button>
            </Stack>
            <Button variant="outlined" fullWidth onClick={() => copy(invoice.invoiceCode)} sx={{ mt: 1.5, borderRadius: 3, textTransform: 'none', color: '#fff', borderColor: 'rgba(255,255,255,0.16)' }}>
              Copy Invoice Code
            </Button>
          </CardContent>
        </Card>

        <Button variant="contained" onClick={recordPayment} sx={{ borderRadius: 999, py: 1.6, textTransform: 'none', fontWeight: 900, bgcolor: '#FFFFFF', color: '#0B0620', '&:hover': { bgcolor: '#F7F7FF' } }}>
          Record Payment and Issue Receipt
        </Button>
      </Stack>
    </Container>
  )
}
